import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, clearIonicStorage } from '../../contexts/AuthContext';
import logo from '../../assets/1Risk-Calibri-Final-1097x400.png';
import './Login.css';

const SUPPORT_URL = 'https://accesso.atlassian.net/servicedesk/customer/portal/1340';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login, getCachedApi } = useAuth();
  const [formData, setFormData] = useState({
    apiName: '',
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('');

  useEffect(() => {
    const cached = getCachedApi();
    if (cached) {
      setFormData((prev) => ({ ...prev, apiName: cached }));
    }
  }, [getCachedApi]);

  // Defense-in-depth: wipe the shared Ionic Storage IndexedDB whenever the
  // login page loads, so any lingering session data (e.g. from a same-origin
  // legacy app landed on via browser back navigation) can't be reused.
  useEffect(() => {
    clearIonicStorage();
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem('portalSessionMessage');
    if (msg) {
      sessionStorage.removeItem('portalSessionMessage');
      setSessionMessage(msg);
      const timer = setTimeout(() => setSessionMessage(''), 60000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const formValid = formData.apiName && formData.username && formData.password;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    const form = e.target;
    const apiName = form.apiName.value;
    const username = form.username.value;
    const password = form.password.value;

    if (!apiName || !username || !password) return;

    setLoading(true);
    try {
      const result = await login(apiName, username, password);
      if (result.success) {
        navigate('/apps');
      } else {
        setError(result.message || 'Login failed');
        setFormData((prev) => ({ ...prev, password: '' }));
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [login, navigate]);

  return (
    <div className="login-container">
      <div className="login-header-bar">
        <img src={logo} alt="1RISK" className="login-header-logo" />
        <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="support-link">
          Support
        </a>
      </div>

      <div className="login-content">
        <div className="login-card">
          <h1>Login</h1>

          {sessionMessage && (
            <div className="alert alert-info">
              {sessionMessage}
              <button className="alert-dismiss" onClick={() => setSessionMessage('')} aria-label="Dismiss">&times;</button>
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="apiName">API Name</label>
              <input
                id="apiName"
                name="apiName"
                type="text"
                value={formData.apiName}
                onChange={handleChange}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="forgot-password-row">
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={!formValid || loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const Login = () => <LoginForm />;

export default Login;
